import type { ComponentProps, ReactNode } from "react";

import { cn } from "@/common/utils";

/** Full-bleed page section on the Grid ground, with the system's side margin and vertical rhythm. */
export function Section({
  className,
  tone = "ground",
  children,
  ...props
}: ComponentProps<"section"> & { tone?: "ground" | "lit" }) {
  return (
    <section
      className={cn(
        "relative px-(--g-margin) py-(--g-section-y)",
        tone === "lit" && "bg-(--g-lit)",
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
}

/** Small mono label above a heading. */
export function Eyebrow({ className, ...props }: ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "m-0 font-mono text-[12.5px] leading-none text-(--g-ink-3)",
        className,
      )}
      {...props}
    />
  );
}

/** Section heading: optional eyebrow, the title and an optional line under it. Left-aligned like the hero. */
export function SectionHeading({
  eyebrow,
  title,
  sub,
  className,
  as: Heading = "h2",
  size = Heading === "h1" ? "display" : "section",
  align = "left",
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  className?: string;
  as?: "h1" | "h2";
  /** Type scale; follows `as` unless set. "page" is the h1 of docs and legal pages. */
  size?: "display" | "page" | "section";
  /** "center" for a section whose content sits centred under it. */
  align?: "left" | "center";
}) {
  return (
    <div
      className={cn(
        "max-w-[44rem]",
        align === "center" && "mx-auto text-center",
        className,
      )}
    >
      {eyebrow && <Eyebrow className="mb-5">{eyebrow}</Eyebrow>}
      <Heading
        className={cn(
          "m-0 font-medium text-balance text-(--g-ink)",
          size === "display" &&
            "text-[40px] leading-none tracking-[-0.035em] md:text-[clamp(40px,4.8vw,70px)]",
          size === "page" &&
            "text-[36px] leading-[1.05] tracking-[-0.03em] md:text-[clamp(36px,3.2vw,48px)]",
          size === "section" &&
            "text-[30px] leading-[1.05] tracking-[-0.03em] md:text-[clamp(30px,3.2vw,48px)]",
        )}
      >
        {title}
      </Heading>
      {sub && (
        <p
          className={cn(
            "mt-5 max-w-[34em] text-[15px] leading-[1.5] text-pretty text-(--g-ink-2) md:text-[17px]",
            align === "center" && "mx-auto",
          )}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

/** Translucent panel; the system's card and container surface. */
export function GlassCard({
  className,
  raised = false,
  ...props
}: ComponentProps<"div"> & { raised?: boolean }) {
  return (
    <div
      className={cn(
        "g-round p-6",
        raised ? "g-glass-raised" : "g-glass",
        className,
      )}
      {...props}
    />
  );
}

/** Grey ground with the card's grain; `lattice` adds the hero's dot grid. */
export function Ground({
  lattice = false,
  className,
  children,
  ...props
}: ComponentProps<"div"> & { lattice?: boolean }) {
  return (
    <div
      className={cn("relative isolate overflow-hidden", className)}
      style={{
        background:
          "radial-gradient(ellipse 80% 70% at 50% 44%, var(--g-lit) 0%, transparent 70%), radial-gradient(ellipse 140% 120% at 50% 50%, transparent 55%, var(--g-shade) 100%), var(--g-card)",
      }}
      {...props}
    >
      <div
        aria-hidden
        className="g-grain pointer-events-none absolute inset-0 -z-20 opacity-50"
      />
      {lattice && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(22, 25, 28, 0.085) 2px, transparent 2.3px)",
            backgroundSize: "12px 12px",
          }}
        />
      )}
      {children}
    </div>
  );
}
